// ============================================
// Centrifuge validation protocol (firmware v0.5.39 `centrifuge ui`)
// ============================================
//
// `centrifuge ui` drives the IMU centrifuge recording and streams NDJSON events.
// Parse each line with `parseFactoryLine` (utils/factoryTest.js) — it returns any
// `{"t":…}` object — then fold events with `reduceCentrifugeState`.
//
// Command sequence (device must be IDLE/STANDBY; we send `ui on` on connect):
//   centrifuge ui   → enter mode (WIPES the impact store, pre-erases flash)
//   centrifuge start→ arm a 30s countdown, then recording auto-begins
//   centrifuge stop → cancel the countdown, or stop a running recording
//   idle            → leave the mode (→ IDLE), emits {"t":"end","mode":"centrifuge"}
//
// Event types: ack | nack | start | phase | progress | countdown | recording |
//   stopped | warning | error | ignored | end. `progress`/`countdown`/`recording`
//   ticks are lossy (may be dropped) — never treat a missing tick as an error.

import { t } from "i18n";

/**
 * Parse the `dump` command output (prose, not NDJSON) for the run's peak g and
 * record count. Firmware format:
 *   [dump] N stored record(s):
 *   [dump] #k @0x… t=… ms | … | peak=X.XX g (x=… y=… z=… mg) | …
 *   [dump] end (M shown)
 * The run peak is the max per-record peak. Returns { records, peakG, shown }.
 */
export function parseDumpPeak(lines) {
  let records = null;
  let shown = null;
  let peakG = null;
  for (const raw of lines || []) {
    const l = String(raw);
    let m;
    if ((m = l.match(/\[dump\]\s+(\d+)\s+stored record/i))) {
      records = Number(m[1]);
    }
    if ((m = l.match(/\[dump\]\s+end\s*\((\d+)\s+shown\)/i))) {
      shown = Number(m[1]);
    }
    if ((m = l.match(/peak=(\d+(?:\.\d+)?)\s*g/i))) {
      const g = Number(m[1]);
      if (!Number.isNaN(g) && (peakG == null || g > peakG)) peakG = g;
    }
  }
  return { records, peakG, shown };
}

/** Max `peakMagG` across the valid exported records (the run peak, in g). */
export function centrifugePeakFromRecords(records) {
  let peak = null;
  for (const r of records || []) {
    if (r?.isValid === false) continue;
    const p = r?.peakMagG;
    if (typeof p === "number" && (peak == null || p > peak)) peak = p;
  }
  return peak;
}

// Flatten the records of one band into a single continuous, full-resolution
// series with a rebuilt time axis in seconds. This is the raw material both the
// full-trace view and any zoom window decimate from — keep it un-decimated so a
// zoom can resolve real detail. Returns { flat, rateHz, fsG, totalSamples,
// durationS }.
export function buildCentrifugeFlat(records, band = "low") {
  const key = band === "high" ? "highG" : "lowG";
  const hzKey = band === "high" ? "highgHz" : "lowgHz";
  const fsKey = band === "high" ? "highgFsG" : "lowgFsG";

  const valid = (records || [])
    .filter((r) => r && r.isValid !== false && Array.isArray(r[key]))
    .sort((a, b) => (a.frameIndex ?? 0) - (b.frameIndex ?? 0));

  const rateHz = valid.find((r) => r[hzKey] != null)?.[hzKey] ?? 960;
  const fsG = valid.find((r) => r[fsKey] != null)?.[fsKey] ?? null;
  const dtS = 1 / rateHz;

  const flat = [];
  let idx = 0;
  for (const r of valid) {
    for (const s of r[key]) {
      flat.push({ t: idx * dtS, x: s.x, y: s.y, z: s.z, mag: s.mag });
      idx += 1;
    }
  }

  const totalSamples = flat.length;
  const durationS = totalSamples > 0 ? (totalSamples - 1) * dtS : 0;
  return { flat, rateHz, fsG, totalSamples, durationS };
}

// Peak-preserving bucket decimation of a flat series, optionally restricted to a
// time window [tStart, tEnd] in seconds. The largest-|mag| real sample per
// bucket survives, so spikes are never dropped — which is what lets a zoom
// reveal detail rather than just stretch coarse points. Returns { points,
// shownSamples, windowSamples }.
export function decimateSeries(
  flat,
  { tStart = null, tEnd = null, maxPoints = 2500 } = {}
) {
  let window = flat;
  if (tStart != null || tEnd != null) {
    const lo = tStart == null ? -Infinity : tStart;
    const hi = tEnd == null ? Infinity : tEnd;
    window = flat.filter((p) => p.t >= lo && p.t <= hi);
  }
  const n = window.length;
  if (n <= maxPoints || maxPoints <= 0) {
    return { points: window, shownSamples: n, windowSamples: n };
  }
  const points = [];
  const bucket = n / maxPoints;
  for (let b = 0; b < maxPoints; b++) {
    const start = Math.floor(b * bucket);
    const end = Math.min(n, Math.floor((b + 1) * bucket));
    if (end <= start) continue;
    let best = window[start];
    let bestMag = Math.abs(best.mag);
    for (let i = start + 1; i < end; i++) {
      const m = Math.abs(window[i].mag);
      if (m > bestMag) {
        best = window[i];
        bestMag = m;
      }
    }
    points.push(best);
  }
  return { points, shownSamples: points.length, windowSamples: n };
}

// Granularity steps for the viewer's per-band detail selector. `binMs` is the
// bin width in milliseconds; 0 = raw (no aggregation), null = auto (chosen from
// zoom + pixel width). Config-driven so the steps can be tuned without touching
// the component.
export const GRANULARITY_STEPS = [
  {
    id: "auto",
    get label() {
      return t("utils.centrifuge.granularity.auto");
    },
    binMs: null,
  },
  {
    id: "raw",
    get label() {
      return t("utils.centrifuge.granularity.raw", { hz: 960 });
    },
    binMs: 0,
  },
  { id: "10ms", label: "10 ms", binMs: 10 },
  { id: "25ms", label: "25 ms", binMs: 25 },
  { id: "50ms", label: "50 ms", binMs: 50 },
  { id: "100ms", label: "100 ms", binMs: 100 },
  { id: "200ms", label: "200 ms", binMs: 200 },
  { id: "300ms", label: "300 ms", binMs: 300 },
  { id: "400ms", label: "400 ms", binMs: 400 },
  { id: "500ms", label: "500 ms", binMs: 500 },
  { id: "800ms", label: "800 ms", binMs: 800 },
  { id: "1s", label: "1 s", binMs: 1000 },
  { id: "2s", label: "2 s", binMs: 2000 },
];

const AXIS_KEYS = ["x", "y", "z", "mag"];

// First index i with flat[i].t >= target (flat is sorted by t).
function lowerBoundT(flat, target) {
  let lo = 0;
  let hi = flat.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (flat[mid].t < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Aggregate a flat series over [tStart, tEnd] into bins of `binSamples` raw
 * samples. Each bin carries per-axis mean / min / max (and n). This never
 * decimates: every raw sample in a bin contributes, so an in-band ripple shows
 * as envelope width (min..max) rather than aliasing into fake slow waves the way
 * "every Nth sample" would. `binSamples <= 1` returns raw points (mean == the
 * sample, zero-width envelope) with `raw: true`.
 *
 * Each point: { t (bin centre), n, x_mean, x_min, x_max, x_range:[min,max], … }.
 */
export function aggregateWindow(
  flat,
  { tStart = null, tEnd = null, binSamples = 1 } = {}
) {
  const total = flat.length;
  const start = tStart == null ? 0 : lowerBoundT(flat, tStart);
  const end = tEnd == null ? total : lowerBoundT(flat, tEnd + 1e-9);
  const step = Math.max(1, Math.floor(binSamples));
  const points = [];
  for (let i = start; i < end; i += step) {
    const j = Math.min(end, i + step);
    const count = j - i;
    if (count <= 0) continue;
    const sum = { x: 0, y: 0, z: 0, mag: 0 };
    const mn = { x: Infinity, y: Infinity, z: Infinity, mag: Infinity };
    const mx = { x: -Infinity, y: -Infinity, z: -Infinity, mag: -Infinity };
    for (let k = i; k < j; k++) {
      const s = flat[k];
      for (const a of AXIS_KEYS) {
        const v = s[a];
        sum[a] += v;
        if (v < mn[a]) mn[a] = v;
        if (v > mx[a]) mx[a] = v;
      }
    }
    const pt = { t: (flat[i].t + flat[j - 1].t) / 2, n: count };
    for (const a of AXIS_KEYS) {
      const mean = sum[a] / count;
      pt[`${a}_mean`] = mean;
      pt[`${a}_min`] = mn[a];
      pt[`${a}_max`] = mx[a];
      pt[`${a}_range`] = [mn[a], mx[a]];
    }
    points.push(pt);
  }
  return {
    points,
    raw: step <= 1,
    binSamples: step,
    windowSamples: end - start,
  };
}

/**
 * Assemble the exported records into one continuous, downsampled waveform series
 * for the given band. Thin wrapper over buildCentrifugeFlat + decimateSeries,
 * kept for callers that want the whole trace in one call.
 */
export function buildCentrifugeSeries(records, band = "low", maxPoints = 2500) {
  const { flat, rateHz, fsG, totalSamples, durationS } = buildCentrifugeFlat(
    records,
    band
  );
  const { points, shownSamples } = decimateSeries(flat, { maxPoints });
  return { points, totalSamples, shownSamples, rateHz, durationS, fsG };
}

/** Fresh, empty centrifuge stream state. */
export function initCentrifugeState() {
  return {
    entered: false, // saw {"t":"start"} OR a cent_status with phase != off
    fw: null,
    arm: null, // { armMs, tickMs, odrHz, recordMs }
    phase: null, // preparing | waiting | countdown | recording
    cancelReason: null, // "countdown_cancelled" when a countdown is cancelled
    wipe: null, // { done, total } flash-wipe progress
    remainingMs: null, // countdown tick
    recording: null, // { elapsedMs, records, bytes, leftRecords }
    run: null, // { elapsedMs, records, peakMg, fault } — THIS run, from cent_status
    capacity: null, // { records, ms }
    stopped: null, // { reason, records, bytes, peakMg, peakG, fault }
    lastRun: null, // previous run's verdict recovered from cent_status (v0.5.82)
    prepFailed: false, // entry wipe failed — arming is refused (v0.5.82)
    ended: false, // saw {"t":"end","mode":"centrifuge"}
    warning: null, // { reason, detail }
    error: null, // { reason, detail }
    nack: null, // { cmd, reason, state }
    ignored: null, // { req, reason } — refused start/stop, cleared on phase change
  };
}

/**
 * Fold one parsed centrifuge event into the running state (pure). Designed for
 * `events.reduce(reduceCentrifugeState, prev)`.
 */
export function reduceCentrifugeState(state, ev) {
  if (!ev || !ev.t) return state;

  switch (ev.t) {
    case "ack":
      // {"t":"ack","cmd":"centrifuge","state":"CENTRIFUGE"} — clear any prior nack.
      return { ...state, nack: null };

    case "nack":
      return {
        ...state,
        nack: {
          cmd: ev.cmd ?? null,
          reason: ev.reason ?? null,
          state: ev.state ?? null,
        },
      };

    case "start":
      // Only the centrifuge mode-start is relevant here.
      if (ev.mode && ev.mode !== "centrifuge") return state;
      return {
        ...initCentrifugeState(),
        entered: true,
        fw: ev.fw ?? null,
        arm: {
          armMs: ev.arm_ms ?? null,
          tickMs: ev.tick_ms ?? null,
          odrHz: ev.odr_hz ?? null,
          recordMs: ev.record_ms ?? null,
        },
      };

    case "phase": {
      const next = {
        ...state,
        phase: ev.v ?? state.phase,
        cancelReason: ev.reason ?? null,
        ignored: null, // a phase edge supersedes any earlier refusal
      };
      if (ev.v === "waiting") {
        // A new waiting phase clears the transient countdown/recording readouts.
        next.remainingMs = null;
        next.recording = null;
        if (ev.capacity_records != null || ev.capacity_ms != null) {
          next.capacity = {
            records: ev.capacity_records ?? null,
            ms: ev.capacity_ms ?? null,
          };
        }
      }
      if (ev.v === "recording") next.remainingMs = null;
      if (ev.v === "preparing") next.wipe = state.wipe;
      return next;
    }

    case "progress":
      return {
        ...state,
        wipe: { done: ev.done ?? null, total: ev.total ?? null },
      };

    case "countdown":
      return { ...state, remainingMs: ev.remaining_ms ?? null };

    case "recording":
      return {
        ...state,
        recording: {
          elapsedMs: ev.elapsed_ms ?? null,
          records: ev.records ?? null,
          bytes: ev.bytes ?? null,
          leftRecords: ev.left_records ?? null,
        },
      };

    case "stopped":
      return {
        ...state,
        stopped: {
          reason: ev.reason ?? null,
          records: ev.records ?? null,
          bytes: ev.bytes ?? null,
          peakMg: ev.peak_mg ?? null,
          peakG: ev.peak_g ?? null,
          fault: ev.fault === true,
        },
      };

    case "warning":
      return {
        ...state,
        warning: { reason: ev.reason ?? null, detail: ev.detail ?? null },
      };

    case "error":
      return {
        ...state,
        error: { reason: ev.reason ?? null, detail: ev.detail ?? null },
        prepFailed: ev.reason === "prepare_failed" ? true : state.prepFailed,
      };

    case "ignored":
      // A refused start/stop (already_counting_down / already_recording /
      // not_recording / prepare_failed). The UI surfaces it; a later phase
      // edge clears it.
      return {
        ...state,
        ignored: { req: ev.req ?? null, reason: ev.reason ?? null },
      };

    case "cent_status": {
      // v0.5.82 resync snapshot — authoritative for everything it carries.
      // Auto-emitted after every phase edge and on 'centstatus', so a client
      // that missed any one-shot event heals here.
      if (ev.phase === "off") {
        return { ...state, entered: false, phase: null };
      }
      return {
        ...state,
        entered: true,
        phase: ev.phase ?? state.phase,
        arm: { ...(state.arm || {}), armMs: ev.arm_ms ?? state.arm?.armMs },
        remainingMs: ev.phase === "countdown" ? ev.remaining_ms ?? null : null,
        run: {
          elapsedMs: ev.elapsed_ms ?? null,
          records: ev.run_records ?? null,
          peakMg: ev.run_peak_mg ?? null,
          fault: ev.fault === true,
        },
        prepFailed: ev.prep_failed === true,
        capacity:
          ev.capacity_records != null
            ? { records: ev.capacity_records, ms: ev.capacity_records * 3000 }
            : state.capacity,
        lastRun:
          ev.last_reason != null
            ? {
                reason: ev.last_reason,
                records: ev.last_records ?? null,
                peakMg: ev.last_peak_mg ?? null,
                fault: ev.last_fault === true,
              }
            : state.lastRun,
      };
    }

    case "end":
      if (ev.mode && ev.mode !== "centrifuge") return state;
      return { ...state, ended: true };

    default:
      return state;
  }
}
